// Applies the signed-in user's account-level language preference
// (core.users.preferred_locale) to the live i18next instance.
//
// Why this exists: the language pickers PERSIST the choice to the account,
// but until this hook nothing ever READ it back at sign-in — so the
// preference only followed the browser's localStorage, not the person.
// Signing in on a new device (or after clearing storage) silently dropped
// back to English. This hook closes the loop: preference precedence is
//
//   core.users.preferred_locale (authenticated) > localStorage > navigator
//
// Mount it once in each app's authenticated shell with `enabled` tied to
// the session being present. It fetches the profile once per mount-cycle,
// flips the language only when the stored preference differs from the
// active locale, and is a no-op for users with no stored preference
// (preferred_locale null = "never chose explicitly").

import { useEffect, useRef } from 'react';
import { getMyProfile } from '@pc/db';

import { setLanguage, currentLocale } from './i18n';
import { isSupportedLocale, normalizeLocale } from './locales';

export function useApplyAccountLocale(enabled: boolean): void {
  // One application per shell mount: after the initial sync the in-page
  // pickers own the language, and re-running on every render would fight
  // a user who just picked a different language locally.
  const applied = useRef(false);

  useEffect(() => {
    if (!enabled || applied.current) return;
    applied.current = true;
    void (async () => {
      try {
        const profile = await getMyProfile();
        const stored = profile?.preferred_locale;
        if (!stored || !isSupportedLocale(normalizeLocale(stored))) return;
        const target = normalizeLocale(stored);
        if (target !== currentLocale()) {
          await setLanguage(target);
        }
      } catch {
        // Best-effort: a failed profile read just leaves the
        // localStorage/navigator-derived language in place.
      }
    })();
  }, [enabled]);
}
